
import { Appointment, Doctor, TimeSlot, TimeSlotGroup } from "../types";
import { addDays, format, isSameDay } from "date-fns";

// Mock Doctors
export const doctors: Doctor[] = [
  {
    id: 1,
    name: "Dr. Sarah Johnson",
    specialty: "Cardiologist",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=250&auto=format&fit=crop",
    bio: "Dr. Johnson is a board-certified cardiologist with over 15 years of experience in treating heart conditions. She specializes in preventive cardiology and heart health management.",
    slotDuration: 15 // Default slot duration in minutes
  },
  {
    id: 2,
    name: "Dr. Michael Chen",
    specialty: "Dermatologist",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?q=80&w=250&auto=format&fit=crop",
    bio: "Dr. Chen is an expert in medical and cosmetic dermatology, focusing on skin cancer prevention and treatment of various skin conditions. He has been practicing for over 10 years.",
    slotDuration: 15
  },
  {
    id: 3,
    name: "Dr. Emily Rodriguez",
    specialty: "Pediatrician",
    image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?q=80&w=250&auto=format&fit=crop",
    bio: "Dr. Rodriguez has dedicated her career to children's healthcare. With a warm approach and 12 years of experience, she provides comprehensive pediatric care from newborns to adolescents.",
    slotDuration: 15
  },
  {
    id: 4,
    name: "Dr. James Wilson",
    specialty: "Orthopedic Surgeon",
    image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?q=80&w=250&auto=format&fit=crop",
    bio: "Dr. Wilson is a specialized orthopedic surgeon with expertise in joint replacement and sports medicine. He has performed over 2,000 successful surgeries in his 20-year career.",
    slotDuration: 15
  },
  {
    id: 5,
    name: "Dr. Aisha Patel",
    specialty: "Neurologist",
    image: "https://images.unsplash.com/photo-1651008376811-b90baee60c1f?q=80&w=250&auto=format&fit=crop",
    bio: "Dr. Patel is a neurologist specializing in headache disorders and stroke prevention. Her patient-centered approach and diagnostic skills have helped countless patients with neurological conditions.",
    slotDuration: 15
  }
];

// Export doctors as mockDoctors for use in dashboard components
export const mockDoctors = doctors;

// Generate time slots with custom duration
export const generateTimeSlots = (date: Date, doctorId?: number): TimeSlotGroup[] => {
  // Get the selected doctor's slot duration or default to 30 minutes
  const doctor = doctorId ? doctors.find(d => d.id === doctorId) : null;
  const slotDuration = doctor?.slotDuration || 30;
  
  // Morning slots with custom duration
  const morningSlots: TimeSlot[] = [];
  for (let i = 9; i < 12; i++) {
    for (let j = 0; j < 60; j += slotDuration) {
      const hour = i.toString().padStart(2, '0');
      const minute = j.toString().padStart(2, '0');
      const time = `${hour}:${minute}`;
      const displayTime = `${i}:${minute === '00' ? '00' : minute} ${i < 12 ? 'AM' : 'PM'}`;
      
      morningSlots.push({
        id: `morning-${time}`,
        time: displayTime,
        available: Math.random() > 0.3 // 70% chance of being available
      });
    }
  }

  // Afternoon slots with custom duration
  const afternoonSlots: TimeSlot[] = [];
  for (let i = 13; i < 16; i++) {
    for (let j = 0; j < 60; j += slotDuration) {
      const hour = i === 13 ? 1 : i === 14 ? 2 : 3;
      const minute = j.toString().padStart(2, '0');
      const time = `${i.toString().padStart(2, '0')}:${minute}`;
      const displayTime = `${hour}:${minute === '00' ? '00' : minute} PM`;
      
      afternoonSlots.push({
        id: `afternoon-${time}`,
        time: displayTime,
        available: Math.random() > 0.4 // 60% chance of being available
      });
    }
  }

  // Evening slots with custom duration
  const eveningSlots: TimeSlot[] = [];
  for (let i = 16; i < 19; i++) {
    for (let j = 0; j < 60; j += slotDuration) {
      const hour = i === 16 ? 4 : i === 17 ? 5 : 6;
      const minute = j.toString().padStart(2, '0');
      const time = `${i.toString().padStart(2, '0')}:${minute}`;
      const displayTime = `${hour}:${minute === '00' ? '00' : minute} PM`;
      
      eveningSlots.push({
        id: `evening-${time}`,
        time: displayTime,
        available: Math.random() > 0.35 // 65% chance of being available
      });
    }
  }

  return [
    {
      label: "Morning",
      slots: morningSlots
    },
    {
      label: "Afternoon",
      slots: afternoonSlots
    },
    {
      label: "Evening",
      slots: eveningSlots
    }
  ];
};

// Mock Appointments
export const appointments: Appointment[] = [
  {
    id: "apt-001",
    doctorId: 1,
    date: format(new Date(), 'yyyy-MM-dd'),
    timeSlot: "9:30 AM",
    patientName: "John Doe",
    patientEmail: "john@example.com",
    patientPhone: "555-123-4567",
    patientAddress: "123 Main St, Anytown",
    patientGender: "Male",
    patientAge: 45,
    status: "confirmed",
    createdAt: new Date(2025, 4, 20).toISOString()
  },
  {
    id: "apt-002",
    doctorId: 2,
    date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    timeSlot: "2:00 PM",
    patientName: "Jane Smith",
    patientEmail: "jane@example.com",
    patientPhone: "555-765-4321",
    patientGender: "Female",
    patientAge: 32,
    status: "confirmed",
    createdAt: new Date(2025, 4, 21).toISOString()
  },
  {
    id: "apt-003",
    doctorId: 3,
    date: format(new Date(), 'yyyy-MM-dd'),
    timeSlot: "4:30 PM",
    patientName: "David Williams",
    patientEmail: "david@example.com",
    patientPhone: "555-987-6543",
    patientAddress: "456 Oak Ave, Somewhere",
    status: "completed",
    createdAt: new Date(2025, 4, 22).toISOString()
  }
];

// Function to check appointment availability
export const isTimeSlotAvailable = (doctorId: number, date: Date, timeSlot: string): boolean => {
  const formattedDate = format(date, 'yyyy-MM-dd');
  
  const conflictingAppointment = appointments.find(
    appointment => 
      appointment.doctorId === doctorId && 
      appointment.date === formattedDate && 
      appointment.timeSlot === timeSlot &&
      appointment.status === 'confirmed'
  );
  
  return !conflictingAppointment;
};

// Function to get doctor's unavailable time slots
export const getDoctorUnavailableTimeSlots = (doctorId: number, date: Date): string[] => {
  const formattedDate = format(date, 'yyyy-MM-dd');
  
  return appointments
    .filter(appointment => 
      appointment.doctorId === doctorId && 
      appointment.date === formattedDate &&
      appointment.status === 'confirmed'
    )
    .map(appointment => appointment.timeSlot);
};

